import { useContext } from 'react';

import { FeatureFlagsContext } from '../services/featureFlags/FeatureFlagsContext';
import { FeatureFlagName } from '@/utils/featureFlags';

interface FeatureFlagsContextType {
  isFeatureEnabled: (featureName: FeatureFlagName) => boolean;
  isSuitableSubstitutesEnabled: boolean;
  isPartFinderEnabled: boolean;
  isPartFinderThumbnailsEnabled: boolean;
  isPartFinderDocumentsEnabled: boolean;
  isSupplementalAssetsEnabled: boolean;
  isCriticalGeometryEnabled: boolean;
  isMetadataSearchEnabled: boolean;
  isUsageAnalyticsEnabled: boolean;
  isImageSearchCroppingDisabled: boolean;
  isSemanticDescriptionGenerationEnabled: boolean;
  isSemanticDescriptionVisibilityEnabled: boolean;
  isComparisonHeatmapEnabled: boolean;
  isTaxonomyEnabled: boolean;
  isTaxonomyAiClassificationEnabled: boolean;
  isUsdIngestEnabled: boolean;
  isSceneIngestionEnabled: boolean;
  areFeatureFlagsLoaded: boolean;
  didFeatureFlagsFail: boolean;
  retryFeatureFlags: () => void;
}

export const useFeatureFlags = (): FeatureFlagsContextType => {
  const context = useContext(FeatureFlagsContext);
  if (!context) {
    throw new Error('useFeatureFlagsContext must be used within a FeatureFlagsProvider');
  }
  return {
    isFeatureEnabled: context.isFeatureEnabled,
    isSuitableSubstitutesEnabled: context.isFeatureEnabled(FeatureFlagName.SUITABLE_SUBSTITUTES),
    isPartFinderEnabled: context.isFeatureEnabled(FeatureFlagName.PART_FINDER_REPORTS),
    isPartFinderThumbnailsEnabled: context.isFeatureEnabled(FeatureFlagName.PART_FINDER_THUMBNAILS),
    isPartFinderDocumentsEnabled: context.isFeatureEnabled(FeatureFlagName.PART_FINDER_DOCUMENTS),
    isSupplementalAssetsEnabled: context.isFeatureEnabled(FeatureFlagName.SUPPLEMENTAL_ASSETS),
    isCriticalGeometryEnabled: context.isFeatureEnabled(FeatureFlagName.CRITICAL_GEOMETRY),
    isMetadataSearchEnabled: context.isFeatureEnabled(FeatureFlagName.METADATA_SEARCH_UI),
    isUsageAnalyticsEnabled: context.isFeatureEnabled(FeatureFlagName.USAGE_ANALYTICS),
    isImageSearchCroppingDisabled: context.isFeatureEnabled(FeatureFlagName.IMAGE_SEARCH_CROPPING_DISABLED),
    isSemanticDescriptionGenerationEnabled: context.isFeatureEnabled(FeatureFlagName.SEMANTIC_DESCRIPTION_GENERATION),
    isSemanticDescriptionVisibilityEnabled: context.isFeatureEnabled(FeatureFlagName.SEMANTIC_DESCRIPTION_VISIBILITY),
    isComparisonHeatmapEnabled: context.isFeatureEnabled(FeatureFlagName.COMPARISON_HEATMAP),
    isTaxonomyEnabled: context.isFeatureEnabled(FeatureFlagName.TAXONOMY),
    isTaxonomyAiClassificationEnabled: context.isFeatureEnabled(FeatureFlagName.TAXONOMY_AI_CLASSIFICATION),
    isUsdIngestEnabled: context.isFeatureEnabled(FeatureFlagName.USD_INGEST),
    isSceneIngestionEnabled: context.isFeatureEnabled(FeatureFlagName.SCENE_INGESTION),
    areFeatureFlagsLoaded: context.areFeatureFlagsLoaded,
    didFeatureFlagsFail: context.didFeatureFlagsFail,
    retryFeatureFlags: context.retryFeatureFlags,
  };
};
